import { Link } from "react-router-dom";
import { Pill, ShieldCheck, Lock, FileLock2, BadgeCheck, Mail } from "lucide-react";

/**
 * Public HIPAA Compliance attestation page.
 *
 * Intentionally HIGH-LEVEL — this is the page anyone on the internet can
 * read. It states *that* we're compliant, *what* the safeguard categories
 * are, and *how to reach us* — but it does not enumerate file paths,
 * library choices, brute-force thresholds, database collection names, or
 * any other detail that would help an attacker plan an intrusion.
 *
 * The detailed vendor due-diligence packet (with code references) lives
 * at /api/admin/compliance/vendor-packet.pdf and requires admin auth.
 * Legitimate vendor reviewers (Surescripts, payment partners, integrating
 * pharmacies) are directed to compliance@glpflo.org to request the full
 * packet under NDA.
 */
export default function HipaaCompliancePage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="hipaa-compliance-page">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-purple-700">Compliance</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          HIPAA Compliance Statement
        </h1>
        <p className="text-slate-500 mb-8">Last updated: February 2026</p>

        {/* Attestation card */}
        <div className="bg-white rounded-2xl border border-purple-100 p-8 mb-6">
          <p className="text-base text-slate-700 leading-relaxed">
            GLPFlo — operated by <strong>GLPFlo LLC</strong> — is a HIPAA-compliant
            telehealth platform. We safeguard your protected health information (PHI) in accordance with the
            <strong> HIPAA Privacy Rule</strong>, <strong>Security Rule</strong>, and <strong>Breach Notification Rule</strong>,
            and maintain Business Associate Agreements with every service provider that may handle PHI on our behalf.
          </p>
        </div>

        {/* Pillar cards */}
        <div className="grid gap-4 sm:grid-cols-2 mb-8">
          <PillarCard
            icon={Lock}
            title="Encryption everywhere"
            body="All PHI is encrypted in transit and at rest using industry-standard algorithms. Backups are encrypted."
          />
          <PillarCard
            icon={FileLock2}
            title="Strict access controls"
            body="Role-based access, multi-factor authentication, and idle-timeout protections limit PHI access to authorized staff on a need-to-know basis."
          />
          <PillarCard
            icon={BadgeCheck}
            title="Audited & monitored"
            body="Every PHI access event is recorded in an append-only audit log. We review activity regularly and have a documented incident-response process."
          />
          <PillarCard
            icon={ShieldCheck}
            title="Vendor BAAs in place"
            body="Each third-party service that may touch PHI (hosting, payments, email, pharmacy) is governed by a signed Business Associate Agreement."
          />
        </div>

        {/* Patient rights */}
        <section className="bg-white rounded-2xl border border-slate-200 p-8 mb-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Your rights under HIPAA
          </h2>
          <p className="text-slate-600 mb-4">As a GLPFlo patient, you have the right to:</p>
          <ul className="list-disc list-inside space-y-2 text-slate-700">
            <li>Access and obtain a copy of your medical records</li>
            <li>Request corrections to inaccurate or incomplete information</li>
            <li>Receive an accounting of disclosures of your PHI</li>
            <li>Request restrictions on how we use or share your PHI</li>
            <li>File a complaint with GLPFlo or with the U.S. Department of Health and Human Services Office for Civil Rights without fear of retaliation</li>
          </ul>
          <p className="text-sm text-slate-500 mt-5">
            See our full <Link to="/privacy-policy" className="text-purple-700 hover:underline font-medium">Privacy Policy</Link> for additional detail on how we collect, use, and protect your information.
          </p>
        </section>

        {/* Contact + vendor packet CTA */}
        <section className="bg-purple-50 border border-purple-200 rounded-2xl p-8" data-testid="hipaa-contact-block">
          <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Get in touch
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <ContactRow
              label="Privacy questions or PHI requests"
              href="mailto:customer.service@glpflo.com"
              email="customer.service@glpflo.com"
            />
            <ContactRow
              label="Suspected security issue"
              href="mailto:customer.service@glpflo.com"
              email="customer.service@glpflo.com"
            />
            <ContactRow
              label="HIPAA / compliance officer"
              href="mailto:customer.service@glpflo.com"
              email="customer.service@glpflo.com"
            />
            <ContactRow
              label="HHS Office for Civil Rights"
              href="https://www.hhs.gov/ocr/complaints/"
              email="hhs.gov/ocr"
              external
            />
          </div>

          <div className="mt-6 pt-6 border-t border-purple-200">
            <p className="text-sm text-slate-700 leading-relaxed">
              <strong>Vendor due-diligence reviewers</strong> — pharmacy partners, payment processors, and
              integrating healthcare networks may request our detailed vendor compliance packet (covering
              control-by-control implementation, BAA registry, and incident-response timeline) by emailing{" "}
              <a href="mailto:customer.service@glpflo.com" className="text-purple-700 font-medium hover:underline">customer.service@glpflo.com</a> with
              your organization name and intended use. The packet is provided under NDA.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

function PillarCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-purple-700" />
        <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function ContactRow({ label, href, email, external = false }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</p>
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-700 hover:underline"
      >
        <Mail className="w-3.5 h-3.5" /> {email}
      </a>
    </div>
  );
}
